import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, IconButton, CircularProgress, Switch, FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import MUIDataTable from 'mui-datatables';
import { useSnackbar } from 'notistack';
import { getAllDocumentTypes, saveDocumentType, deleteDocumentType } from '../../../store/actions/documentActions';
import { RootState } from '../../../store/reducers';

const DocumentTypes = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const documentTypes = useSelector((state: RootState) => state.cadastros.documentTypes);
    const isFetching = useSelector((state: RootState) => state.cadastros.isFetchingDocumentTypes);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editItem, setEditItem] = useState<iDocumentType | null>(null);
    const [formData, setFormData] = useState<iDocumentType>({ name: '', description: '', required: false, active: true, order: 0 });
    const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

    useEffect(() => {
        dispatch(getAllDocumentTypes() as any);
    }, [dispatch]);

    const handleOpen = (item?: iDocumentType) => {
        if (item) {
            setEditItem(item);
            setFormData({ name: item.name || '', description: item.description || '', required: item.required || false, active: item.active !== false, order: item.order || 0 });
        } else {
            setEditItem(null);
            setFormData({ name: '', description: '', required: false, active: true, order: 0 });
        }
        setDialogOpen(true);
    };

    const handleClose = () => {
        setDialogOpen(false);
        setEditItem(null);
    };

    const handleSave = async () => {
        try {
            const payload: iDocumentType = editItem ? { ...formData, id: editItem.id } : { ...formData };
            await dispatch(saveDocumentType(payload) as any);
            handleClose();
            enqueueSnackbar('Salvo com sucesso!', { variant: 'success' });
        } catch {
            enqueueSnackbar('Erro ao salvar', { variant: 'error' });
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await dispatch(deleteDocumentType(id) as any);
            setDeleteConfirm(null);
            enqueueSnackbar('Excluido com sucesso!', { variant: 'success' });
        } catch {
            enqueueSnackbar('Erro ao excluir. Tipo pode estar em uso.', { variant: 'error' });
            setDeleteConfirm(null);
        }
    };

    const columns = [
        { name: 'id', label: 'ID', options: { display: false, filter: false, sort: true } },
        { name: 'name', label: 'Nome', options: { filter: true, sort: true } },
        { name: 'description', label: 'Descrição', options: { filter: false, sort: false } },
        {
            name: 'required', label: 'Obrigatório', options: {
                filter: true, sort: true,
                customBodyRenderLite: (dataIndex: number) => documentTypes[dataIndex]?.required ? 'Sim' : 'Não'
            }
        },
        {
            name: 'active', label: 'Ativo', options: {
                filter: true, sort: true,
                customBodyRenderLite: (dataIndex: number) => documentTypes[dataIndex]?.active !== false ? 'Sim' : 'Não'
            }
        },
        { name: 'order', label: 'Ordem', options: { filter: false, sort: true } },
        {
            name: 'actions', label: 'Ações', options: {
                filter: false, sort: false,
                customBodyRenderLite: (dataIndex: number) => {
                    const item = documentTypes[dataIndex];
                    return (
                        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setDeleteConfirm(item.id!); }}>
                            <DeleteIcon />
                        </IconButton>
                    );
                }
            }
        }
    ];

    const options = {
        filterType: 'checkbox' as const,
        selectableRows: 'none' as const,
        responsive: 'standard' as const,
        rowsPerPage: 15,
        rowsPerPageOptions: [15, 30, 50],
        textLabels: {
            body: { noMatch: 'Nenhum registro encontrado', toolTip: 'Ordenar' },
            pagination: { next: 'Próxima', previous: 'Anterior', rowsPerPage: 'Linhas por página:', displayRows: 'de' },
            toolbar: { search: 'Buscar', downloadCsv: 'Download CSV', print: 'Imprimir', viewColumns: 'Colunas', filterTable: 'Filtrar' },
            filter: { all: 'Todos', title: 'Filtros', reset: 'Limpar' },
        },
        onRowClick: (_rowData: any, rowMeta: any) => {
            handleOpen(documentTypes[rowMeta.dataIndex]);
        },
    };

    if (isFetching) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">Tipos de Documento</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
                    Novo Tipo
                </Button>
            </Box>

            <Paper>
                <MUIDataTable title="" data={documentTypes || []} columns={columns} options={options} />
            </Paper>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{editItem ? 'Editar Tipo de Documento' : 'Novo Tipo de Documento'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Nome"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Descrição"
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            fullWidth
                            multiline
                            rows={2}
                        />
                        <TextField
                            label="Ordem"
                            type="number"
                            value={formData.order}
                            onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                            fullWidth
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.required || false}
                                    onChange={(e) => setFormData(prev => ({ ...prev, required: e.target.checked }))}
                                />
                            }
                            label="Obrigatório"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.active !== false}
                                    onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                                />
                            }
                            label="Ativo"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" disabled={!formData.name}>Salvar</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirm !== null} onClose={() => setDeleteConfirm(null)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <Typography>Tem certeza que deseja excluir este tipo de documento?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                    <Button onClick={() => deleteConfirm !== null && handleDelete(deleteConfirm)} color="error" variant="contained">Excluir</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DocumentTypes;
